<script setup lang="ts">
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import type { ContactFormData } from '~/types'

const { t } = useI18n()
const route = useRoute()

/*
 * Conditional positioning based on route. `--cta-bar-height` is published by
 * the blog's sticky CTA bar (components/blog/CtaBar.vue) and is 0 everywhere
 * else, so the bubble rides above the bar while it is on screen.
 */
const chatButtonClasses = computed(() => {
  const isCvSection = route.path.startsWith('/cv')
  return isCvSection
    ? 'fixed bottom-[calc(0.5rem+var(--cta-bar-height,0px))] right-2 z-50 flex items-center justify-end md:bottom-[calc(8rem+var(--cta-bar-height,0px))] md:right-8'
    : 'fixed bottom-[calc(0.5rem+var(--cta-bar-height,0px))] right-2 z-50 flex items-center justify-end md:bottom-[calc(1rem+var(--cta-bar-height,0px))] md:right-8'
})

// Form validation schema
const schema = yup.object({
  name: yup.string()
    .required(t('common.validators.required'))
    .min(2, t('chat.widget.nameMinLength'))
    .max(50, t('chat.widget.nameMaxLength')),
  email: yup.string()
    .required(t('common.validators.required'))
    .email(t('common.validators.email')),
  subject: yup.string()
    .required(t('common.validators.required')),
  message: yup.string()
    .required(t('common.validators.required'))
})

// Form state
const { defineField, handleSubmit, errors, resetForm } = useForm<ContactFormData>({
  validationSchema: schema
})

const [name, nameAttrs] = defineField('name')
const [email, emailAttrs] = defineField('email')
const [subject, subjectAttrs] = defineField('subject')
const [message, messageAttrs] = defineField('message')

// Widget state
const isOpen = ref(false)
const isSubmitting = ref(false)
const isSubmitted = ref(false)

// Subject dropdown state
const selectedSubject = ref('')
const customSubject = ref('')

// Subject selection options and their translations
const subjectOptions = computed(() => ({
  general: t('contact.form.subjects.general'),
  support: t('contact.form.subjects.support'),
  billing: t('contact.form.subjects.billing'),
  feature: t('contact.form.subjects.feature'),
  bug: t('contact.form.subjects.bug'),
  feedback: t('contact.form.subjects.feedback'),
  other: t('contact.form.subjects.other')
}))

// Handle subject selection change
function handleSubjectChange() {
  if (selectedSubject.value === 'other') {
    subject.value = customSubject.value
  } else if (selectedSubject.value && subjectOptions.value[selectedSubject.value as keyof typeof subjectOptions.value]) {
    subject.value = subjectOptions.value[selectedSubject.value as keyof typeof subjectOptions.value]
  } else {
    subject.value = ''
  }
}

// Watch for changes in custom subject when "other" is selected
watch(customSubject, (newValue) => {
  if (selectedSubject.value === 'other') {
    subject.value = newValue
  }
})

// Toggle widget
const toggleWidget = () => {
  isOpen.value = !isOpen.value
  if (!isOpen.value) {
    // Reset form when closing
    resetForm()
    isSubmitted.value = false
    selectedSubject.value = ''
    customSubject.value = ''
  }
}

// Close widget when clicking outside
const widgetRef = ref<HTMLDivElement>()
onClickOutside(widgetRef, () => {
  if (isOpen.value) {
    isOpen.value = false
    resetForm()
    isSubmitted.value = false
    selectedSubject.value = ''
    customSubject.value = ''
  }
})

// Submit form
const onSubmit = handleSubmit(async (values) => {
  try {
    isSubmitting.value = true

    const { data: _data } = await $fetch('/api/contact/message', {
      method: 'POST',
      body: values
    })

    isSubmitted.value = true
    resetForm()
    selectedSubject.value = ''
    customSubject.value = ''

    // Auto close after 3 seconds
    setTimeout(() => {
      isOpen.value = false
      isSubmitted.value = false
    }, 3000)

  } catch (error: unknown) {
    console.error('Failed to send message:', error)

    // Show error notification
    const errorMessage = (error && typeof error === 'object' && 'data' in error && error.data && typeof error.data === 'object' && 'statusMessage' in error.data)
      ? (error.data as { statusMessage: string }).statusMessage
      : t('chat.widget.errorDefault')

    // You can use your notification system here
    alert(errorMessage)
  } finally {
    isSubmitting.value = false
  }
})

// Character count for message
const messageLength = computed(() => message.value?.length || 0)
const maxMessageLength = 1000
</script>

<template>
  <!-- Hidden on phones: the bubble sat over the homepage chat's answer box and
       other bottom-of-screen controls. /contact stays linked from the footer. -->
  <div
    ref="widgetRef"
    class="sofax fixed bottom-[calc(1.5rem+var(--cta-bar-height,0px))] right-6 z-50 max-md:hidden md:bottom-[calc(5rem+var(--cta-bar-height,0px))] md:right-10"
  >
    <!-- Chat Widget -->
    <Transition name="slide-up">
      <div
        v-show="isOpen"
        class="mb-4 w-80 overflow-hidden rounded-[0.625rem] border border-line bg-white shadow-lift sm:w-96 dark:border-gray-700 dark:bg-gray-800"
      >
        <!-- Header -->
        <div class="flex items-start justify-between gap-4 border-b border-line p-5 dark:border-gray-700">
          <div>
            <div class="font-title text-xl font-bold text-ink dark:text-white">{{ t('chat.widget.title') }}</div>
            <div class="mt-1 text-sm text-muted dark:text-gray-400">{{ t('chat.widget.subtitle') }}</div>
          </div>
          <button
            type="button"
            class="social-link !size-9 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            :aria-label="t('chat.widget.closeAriaLabel')"
            @click="toggleWidget"
          >
            <i class="bi bi-x-lg text-base" aria-hidden="true" />
          </button>
        </div>

        <!-- Content -->
        <div class="p-5">
          <!-- Success Message -->
          <div v-if="isSubmitted" class="grid place-items-center gap-3 py-4 text-center">
            <SofaxIconTile icon="bi-check2" round size="sm" />
            <div class="font-title text-lg font-bold text-ink dark:text-white">{{ t('chat.widget.successTitle') }}</div>
            <p class="!text-sm">{{ t('chat.widget.successMessage') }}</p>
          </div>

          <!--
            Contact form. vee-validate's field attrs (onBlur etc.) must land on
            the native controls, so these are plain elements with the Sofax
            field classes instead of the wrapper components.
          -->
          <form v-else class="grid gap-4" @submit="onSubmit">
            <!-- Name Field -->
            <div>
              <label for="chat-name" class="field-label !mb-2 !text-sm">
                {{ t('chat.widget.nameLabel') }}<span class="text-accent"> *</span>
              </label>
              <input
                id="chat-name"
                v-model="name"
                v-bind="nameAttrs"
                type="text"
                autocomplete="name"
                class="field-input !px-5 !py-3 !text-sm"
                :class="{ 'field-error': errors.name }"
                :aria-invalid="errors.name ? 'true' : undefined"
                :placeholder="t('chat.widget.namePlaceholder')"
              >
              <span v-if="errors.name" class="field-hint !text-xs !text-red-600 dark:!text-red-400">
                {{ errors.name }}
              </span>
            </div>

            <!-- Email Field -->
            <div>
              <label for="chat-email" class="field-label !mb-2 !text-sm">
                {{ t('chat.widget.emailLabel') }}<span class="text-accent"> *</span>
              </label>
              <input
                id="chat-email"
                v-model="email"
                v-bind="emailAttrs"
                type="email"
                autocomplete="email"
                class="field-input !px-5 !py-3 !text-sm"
                :class="{ 'field-error': errors.email }"
                :aria-invalid="errors.email ? 'true' : undefined"
                :placeholder="t('chat.widget.emailPlaceholder')"
              >
              <span v-if="errors.email" class="field-hint !text-xs !text-red-600 dark:!text-red-400">
                {{ errors.email }}
              </span>
            </div>

            <!-- Subject Field -->
            <div>
              <label for="chat-subject" class="field-label !mb-2 !text-sm">
                {{ t('contact.form.subject') }}<span class="text-accent"> *</span>
              </label>
              <select
                id="chat-subject"
                v-model="selectedSubject"
                v-bind="subjectAttrs"
                class="field-select !px-5 !py-3 !pr-12 !text-sm"
                :class="{ 'field-error': errors.subject }"
                :aria-invalid="errors.subject ? 'true' : undefined"
                @change="handleSubjectChange"
              >
                <option value="">{{ t('contact.form.subjects.placeholder') }}</option>
                <option value="general">{{ t('contact.form.subjects.general') }}</option>
                <option value="support">{{ t('contact.form.subjects.support') }}</option>
                <option value="billing">{{ t('contact.form.subjects.billing') }}</option>
                <option value="feature">{{ t('contact.form.subjects.feature') }}</option>
                <option value="bug">{{ t('contact.form.subjects.bug') }}</option>
                <option value="feedback">{{ t('contact.form.subjects.feedback') }}</option>
                <option value="other">{{ t('contact.form.subjects.other') }}</option>
              </select>
              <span v-if="errors.subject" class="field-hint !text-xs !text-red-600 dark:!text-red-400">
                {{ errors.subject }}
              </span>
            </div>

            <!-- Custom Subject Field - shown when "Other" is selected -->
            <div v-if="selectedSubject === 'other'">
              <label for="chat-custom-subject" class="field-label !mb-2 !text-sm">
                {{ t('contact.form.customSubject') }}<span class="text-accent"> *</span>
              </label>
              <input
                id="chat-custom-subject"
                v-model="customSubject"
                type="text"
                class="field-input !px-5 !py-3 !text-sm"
                :placeholder="t('contact.form.customSubjectPlaceholder')"
              >
            </div>

            <!-- Message Field -->
            <div>
              <label for="chat-message" class="field-label !mb-2 !text-sm">
                {{ t('chat.widget.messageLabel') }}<span class="text-accent"> *</span>
              </label>
              <textarea
                id="chat-message"
                v-model="message"
                v-bind="messageAttrs"
                rows="4"
                :maxlength="maxMessageLength"
                class="field-textarea !min-h-28 !px-5 !py-3 !text-sm"
                :class="{ 'field-error': errors.message }"
                :aria-invalid="errors.message ? 'true' : undefined"
                :placeholder="t('chat.widget.messagePlaceholder')"
              />
              <div class="mt-1 flex items-start justify-between gap-3">
                <span v-if="errors.message" class="field-hint !mt-0 !text-xs !text-red-600 dark:!text-red-400">
                  {{ errors.message }}
                </span>
                <span class="field-hint !mt-0 ml-auto !text-xs">{{ messageLength }}/{{ maxMessageLength }}</span>
              </div>
            </div>

            <!-- Submit Button -->
            <SofaxBtn
              type="submit"
              variant="accent"
              block
              :label="isSubmitting ? t('chat.widget.submittingButton') : t('chat.widget.submitButton')"
              :disabled="isSubmitting"
            />
          </form>
        </div>
      </div>
    </Transition>

    <!-- Chat Button -->
    <div :class="chatButtonClasses">
      <button
        class="flex size-14 items-center justify-center rounded-full bg-ink text-white shadow-lift transition-all duration-300 hover:bg-accent focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/30 dark:bg-white dark:text-ink dark:hover:bg-accent dark:hover:text-white"
        :aria-label="isOpen ? t('chat.widget.closeAriaLabel') : t('chat.widget.openAriaLabel')"
        @click="toggleWidget"
      >
        <div class="relative flex size-6 items-center justify-center overflow-hidden">
          <!-- Chat Icon -->
          <Transition name="icon-slide-up">
            <svg
              v-show="!isOpen"
              class="size-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </Transition>

          <!-- Close Icon -->
          <Transition name="icon-slide-down">
            <svg
              v-show="isOpen"
              class="size-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Transition>
        </div>
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Transitions */
.scale-enter-active,
.scale-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.scale-enter-from,
.scale-leave-to {
  transform: scale(0.8);
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

/* Icon transitions */
.icon-slide-up-enter-active,
.icon-slide-up-leave-active,
.icon-slide-down-enter-active,
.icon-slide-down-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  left: 50%;
  top: 50%;
  transform-origin: center;
}

/* Chat icon (slides up when leaving, down when entering) */
.icon-slide-up-enter-from {
  transform: translate(-50%, 50%) rotate(-180deg) scale(0.8);
  opacity: 0;
}

.icon-slide-up-enter-to {
  transform: translate(-50%, -50%) rotate(0deg) scale(1);
  opacity: 1;
}

.icon-slide-up-leave-from {
  transform: translate(-50%, -50%) rotate(0deg) scale(1);
  opacity: 1;
}

.icon-slide-up-leave-to {
  transform: translate(-50%, -150%) rotate(180deg) scale(0.8);
  opacity: 0;
}

/* Close icon (slides down when leaving, up when entering) */
.icon-slide-down-enter-from {
  transform: translate(-50%, -150%) rotate(180deg) scale(0.8);
  opacity: 0;
}

.icon-slide-down-enter-to {
  transform: translate(-50%, -50%) rotate(0deg) scale(1);
  opacity: 1;
}

.icon-slide-down-leave-from {
  transform: translate(-50%, -50%) rotate(0deg) scale(1);
  opacity: 1;
}

.icon-slide-down-leave-to {
  transform: translate(-50%, 50%) rotate(-180deg) scale(0.8);
  opacity: 0;
}
</style>
